// Logique PURE de la Mine premium (refonte 21/07) : cadrage caméra x3 (Lot 1), puis timeline du
// suspens de creuse (Lot 3) et layout du sol unique (Lot 2). ZÉRO dépendance DOM/canvas/réseau ->
// testable en node (tests/mine-suspense). Consommé par mine.ts (contrôleur, horloge murale) et
// mine-render.ts (peinture). Purement cosmétique : jamais dans la simulation, le hash ou le réseau.
import { rarityColor, type Rarity } from './mine-crack'

// --- Cadrage caméra (Lot 1) -----------------------------------------------------------------
// Le canvas fait 1344x816 px écran ; en zoom x3 le viewport couvre 448x272 px MONDE (48 px/case).
// X suit le coureur (cible clampée + lissage exponentiel) ; Y est FIXE : MINE_CAM_Y pose le
// dessus du sol (rangée 13 -> y monde 624) aux ~2/3 de l'écran (voûte au-dessus, profondeur
// dessous), et le bas du viewport reste dans la salle (443 + 272 = 715 <= 768).
export const MINE_ZOOM = 3
export const MINE_VIEW_W = 1344 / MINE_ZOOM // 448
export const MINE_VIEW_H = 816 / MINE_ZOOM // 272
export const MINE_CAM_Y = 443 // cadrage étage 1 : sol écran (624-443)*3 = 543 ≈ 2/3 de 816
export const MINE_CAM_X_MAX = 28 * 48 - MINE_VIEW_W // 896

// --- Profondeur (chantier 3 étages) : la caméra PLONGE avec le coureur ------------------------
// Étage k -> rangée de sol DEPTH_FLOORS[k-1] (13/15/17, miroir de mine-scene) ; le cadrage pose
// TOUJOURS le sol de l'étage courant aux ~2/3 de l'écran (même sensation à chaque étage).
const FLOORS = [13, 15, 17] as const

/** Étage (1..3) d'une rangée de la salle : marche OU sol de l'étage. */
export function depthOfRow(row: number): 1 | 2 | 3 {
  return row <= 13 ? 1 : row <= 15 ? 2 : 3
}

/** Cible caméra Y de l'étage : son sol aux ~2/3 de l'écran (443 / 539 / 635). */
export function camYForDepth(depth: 1 | 2 | 3): number {
  return FLOORS[depth - 1]! * 48 - 181
}

/** Cible de la caméra X : centre le focus (px monde), clampée aux bords de la salle. */
export function mineCamTargetX(focusXWorld: number): number {
  const t = focusXWorld - MINE_VIEW_W / 2
  return t < 0 ? 0 : t > MINE_CAM_X_MAX ? MINE_CAM_X_MAX : t
}

/** Lissage exponentiel indépendant du framerate (demi-vie ~90 ms) : ne dépasse jamais la cible. */
export function mineCamStep(current: number, target: number, dtMs: number): number {
  if (dtMs <= 0) return current
  const k = 1 - Math.pow(0.5, dtMs / 90)
  return current + (target - current) * k
}

/** Conversion monde (px, 48/case) -> écran (px canvas) via caméra + zoom. */
export function worldToScreen(wx: number, wy: number, cam: { x: number; y: number; zoom: number }): { x: number; y: number } {
  return { x: (wx - cam.x) * cam.zoom, y: (wy - cam.y) * cam.zoom }
}

/** PRNG déterministe (mulberry32) : mêmes décors et même sol à chaque visite. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// --- Timeline du suspens de creuse (Lot 3) ----------------------------------------------------
// DEUX horloges, parce que la réponse serveur arrive quand elle veut : `t` court depuis le COUP
// de pioche (fissures + punch caméra démarrent tout de suite -> la latence réseau est invisible),
// `tR` court depuis la RÉPONSE (paliers de rareté, séisme, éclatement). Sans réponse, la lueur
// pulse en blanc SANS FIN ; dès la réponse, elle monte palier par palier (blanc -> bleu -> violet
// -> or) jusqu'à l'éclatement — c'est le suspens « ouverture de coffre » : dès que ça vire au
// bleu, on sait qu'on tient au moins un rare. Bornes en TEMPS (ms), jamais en frames.
export interface SuspenseClock { t: number; tR: number | null; rarity: Rarity | null }

const BLANC = '#e8e4f2'
export const SUSPENSE_PALIERS: Record<Rarity, { color: string; at: number }[]> = {
  courant: [{ color: BLANC, at: 0 }],
  rare: [{ color: BLANC, at: 0 }, { color: rarityColor('rare'), at: 450 }],
  epique: [{ color: BLANC, at: 0 }, { color: rarityColor('rare'), at: 400 }, { color: rarityColor('epique'), at: 900 }],
  mega: [
    { color: BLANC, at: 0 }, { color: rarityColor('rare'), at: 400 },
    { color: rarityColor('epique'), at: 900 }, { color: rarityColor('mega'), at: 1500 },
  ],
}

/** Instant de l'ÉCLATEMENT (sur tR) : un courant reste nerveux, un méga fait durer la montée. */
export function suspenseBurstAt(rarity: Rarity): number {
  switch (rarity) {
    case 'courant': return 900
    case 'rare': return 1400
    case 'epique': return 1900
    case 'mega': return 2400
  }
}

const RELEASE_MS = 260 // relâche caméra après l'éclatement (et fin du suspens)
const PUNCH_MS = 220 // punch de zoom au coup de pioche

/** Paliers atteints à tR (>= 1 dès la réponse : le palier blanc est à 0). */
function reachedCount(rarity: Rarity, tR: number): number {
  const paliers = SUSPENSE_PALIERS[rarity]
  let n = 0
  for (const p of paliers) if (tR >= p.at) n++
  return n
}

/** Lueur qui filtre des fissures : couleur du dernier palier atteint + intensité qui monte. */
export function suspenseGlow(c: SuspenseClock): { color: string; intensity: number } {
  if (c.tR === null || c.rarity === null) {
    // v2 (21/07 nuit) : la lueur NAÎT à l'ouverture des fissures (3e coup de pioche), pas avant —
    // pendant les coups, seule la pierre travaille. Montée douce sur ~900 ms puis pulse blanc.
    const warm = Math.max(0, Math.min(1, (c.t - CRACK_START_MS) / 900))
    return { color: BLANC, intensity: warm * (0.35 + 0.1 * Math.sin((Math.PI * 2 * c.t) / 640)) }
  }
  const paliers = SUSPENSE_PALIERS[c.rarity]
  const idx = Math.max(0, reachedCount(c.rarity, c.tR) - 1)
  const k = Math.min(1, c.tR / suspenseBurstAt(c.rarity))
  return { color: paliers[idx]!.color, intensity: 0.3 + 0.7 * k }
}

/** Stades de propagation des fissures (0..3), sur l'horloge du COUP. v2 : les fissures n'existent
 *  qu'APRÈS le 3e coup de pioche (CRACK_START_MS) ; seuils dérivés de crackSegments (~6 et ~12). */
export function suspenseCrackStage(t: number): 0 | 1 | 2 | 3 {
  return t >= 1630 ? 3 : t >= 1150 ? 2 : t >= CRACK_START_MS ? 1 : 0
}

// --- SÉQUENCE v2 (21/07 nuit, « animation superbe ») : la creuse devient une mise en scène -----
// 0-660 ms : TROIS coups de pioche cadencés (flash d'impact + onde de choc + gravats à chaque coup,
// sons crackStrike côté contrôleur). Le 3e coup OUVRE le sol : les fissures se propagent ensuite en
// CONTINU segment par segment (plus par paquets), la lueur naît et monte, les paliers de rareté
// envoient des ONDES, et les 400 dernières ms avant l'éclatement IMPLOSENT vers un point blanc.
export const PICK_HITS_MS = [60, 360, 660] as const
export const CRACK_START_MS = 660
export const CRACK_FULL_MS = 2000
export const CRACK_SEGMENTS = 18 // 9 branches x 2 segments, révélés progressivement

/** Nombre de coups de pioche déjà donnés à t (0..3). PUR. */
export function pickHitsDone(t: number): 0 | 1 | 2 | 3 {
  let n: 0 | 1 | 2 | 3 = 0
  for (const h of PICK_HITS_MS) if (t >= h) n = (n + 1) as 1 | 2 | 3
  return n
}
/** Flash du DERNIER coup de pioche : {i, p 0..1 sur 220 ms} ou null hors fenêtre. PUR. */
export function pickHitFlash(t: number): { i: number; p: number } | null {
  for (let i = PICK_HITS_MS.length - 1; i >= 0; i--) {
    const d = t - PICK_HITS_MS[i]!
    if (d >= 0 && d < 220) return { i, p: d / 220 }
  }
  return null
}
/** Fissures CONTINUES : nombre (flottant) de segments révélés, 0 avant le 3e coup, CRACK_SEGMENTS
 *  au complet à CRACK_FULL_MS — le segment de tête « pousse » (partie fractionnaire). PUR. */
export function crackSegments(t: number): number {
  if (t < CRACK_START_MS) return 0
  return Math.min(CRACK_SEGMENTS, CRACK_SEGMENTS * (t - CRACK_START_MS) / (CRACK_FULL_MS - CRACK_START_MS))
}
/** Onde du dernier palier franchi : {palier, p 0..1 sur 450 ms} ou null (aucun palier / onde finie). PUR. */
export function palierWave(c: SuspenseClock): { palier: number; p: number } | null {
  if (c.tR === null || c.rarity === null) return null
  const paliers = SUSPENSE_PALIERS[c.rarity]
  const idx = reachedCount(c.rarity, c.tR) - 1
  if (idx < 0) return null
  const since = c.tR - paliers[idx]!.at
  return since <= 450 ? { palier: idx, p: since / 450 } : null
}
/** Implosion pré-éclatement : 0 loin du boom, monte à 1 sur les 400 dernières ms. PUR. */
export function implosionP(c: SuspenseClock): number {
  if (c.tR === null || c.rarity === null) return 0
  const d = suspenseBurstAt(c.rarity) - c.tR
  if (d <= 0) return 1
  return d >= 400 ? 0 : 1 - d / 400
}
/** Embrasement MÉGA : 1 au franchissement du palier or, s'éteint sur 900 ms. 0 pour le reste. PUR. */
export function megaBlaze(c: SuspenseClock): number {
  if (c.rarity !== 'mega' || c.tR === null) return 0
  const paliers = SUSPENSE_PALIERS.mega
  const last = paliers[paliers.length - 1]!
  const since = c.tR - last.at
  if (since < 0) return 0
  return since < 300 ? 1 : Math.max(0, 1 - (since - 300) / 600)
}

/** Zoom caméra : punch 3 -> 3.35 (ease-out) au coup, tenu, relâche linéaire après l'éclatement. */
export function suspenseZoom(c: SuspenseClock): number {
  const MAX = 3.35
  if (c.tR !== null && c.rarity !== null) {
    const burst = suspenseBurstAt(c.rarity)
    if (c.tR >= burst) {
      const k = Math.min(1, (c.tR - burst) / RELEASE_MS)
      return MAX - (MAX - 3) * k
    }
  }
  const k = Math.min(1, c.t / PUNCH_MS)
  return 3 + (MAX - 3) * (1 - Math.pow(1 - k, 3))
}

/** Tremblement (px monde) : bref au punch, puis séisme croissant par palier, silence à l'éclatement. */
export function suspenseShake(c: SuspenseClock): number {
  if (c.tR !== null && c.rarity !== null) {
    if (c.tR >= suspenseBurstAt(c.rarity)) return 0
    return 0.4 + 0.55 * reachedCount(c.rarity, c.tR)
  }
  return c.t < PUNCH_MS ? 0.8 : 0
}

// --- Caméra du PUNCH de suspense (fix multi-étages 21/07 nuit) --------------------------------
// La salle P3 fait 24 rangées (MINE_ROWS_TOTAL de mine-scene) : le clamp de l'ancienne salle de
// 16 rangées cadrait l'étage 2 au ras du bas d'écran et jouait TOUTE l'animation de l'étage 3
// hors champ (« ne fonctionne que sur la première ligne »). Fonction PURE (testée par étage) :
// zoom + glissement vers la case (45 % : la case est franchement recentrée quel que soit l'étage)
// + séisme, clampée à la salle PROFONDE. `wob` = les deux sinus du séisme, fournis par l'appelant
// (l'horloge murale reste chez le contrôleur).
export const MINE_ROOM_W = 28 * 48   // 1344
export const MINE_ROOM_H = 24 * 48   // 1152 : salle PROFONDE (miroir mine-scene.MINE_ROWS_TOTAL)
export function suspensePunchCam(
  c: SuspenseClock, frozen: { x: number; y: number },
  holeCx: number, holeTopY: number, wob: { sx: number; sy: number },
): { x: number; y: number; zoom: number } {
  const zoom = suspenseZoom(c)
  const shake = suspenseShake(c)
  const p = Math.min(1, (zoom - MINE_ZOOM) / 0.35)
  const ccx = frozen.x + MINE_VIEW_W / 2, ccy = frozen.y + MINE_VIEW_H / 2
  const cx2 = ccx + (holeCx - ccx) * 0.45 * p
  const cy2 = ccy + (holeTopY - ccy) * 0.45 * p
  const vw = 1344 / zoom, vh = 816 / zoom
  const cl = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v)
  return {
    x: cl(cx2 - vw / 2 + wob.sx * shake, 0, MINE_ROOM_W - vw),
    y: cl(cy2 - vh / 2 + wob.sy * shake, 0, MINE_ROOM_H - vh),
    zoom,
  }
}

/** Index du dernier palier atteint (0 = blanc), -1 tant que la réponse n'est pas là. Sert aux
 *  déclencheurs AUDIO du contrôleur : un tick à chaque CHANGEMENT de palier. */
export function suspensePalier(c: SuspenseClock): number {
  if (c.tR === null || c.rarity === null) return -1
  return reachedCount(c.rarity, c.tR) - 1
}

/** Le suspens est-il terminé (éclatement + relâche joués) ? Sans réponse : jamais. */
export function suspenseOver(c: SuspenseClock): boolean {
  return c.tR !== null && c.rarity !== null && c.tR >= suspenseBurstAt(c.rarity) + RELEASE_MS
}

/** Skip (tap pendant le suspens) : la valeur à donner à tR pour éclater à la frame suivante. */
export function suspenseSkipTR(rarity: Rarity): number {
  return suspenseBurstAt(rarity) - 1
}

// --- La PLUIE D'OR jouable (Mine fun, lot C) --------------------------------------------------
// Quand le loot est un sac de pièces (type 'coins'), le serveur a DÉJÀ tout crédité : la pluie
// est un SPECTACLE INTERACTIF, pas une économie parallèle. Plan pur : n pièces dont la somme des
// valeurs vaut EXACTEMENT coinsGranted ; physique pure : jaillissement balistique depuis le trou,
// gravité, rebonds amortis, pose SUR le sol (jamais dedans), murs, ramassage par PROXIMITÉ du
// coureur. Positions en px MONDE, horloge en ms — cosmétique client, jamais dans la sim.
export interface RainCoin { x: number; y: number; vx: number; vy: number; value: number; taken: boolean }

const RAIN_G = 0.0005 // gravité (px monde / ms²)
const RAIN_FLOOR = 618 // repos : dessus du sol (624) moins le rayon de la pièce
const RAIN_GRAB = 26 // rayon de ramassage autour du centre du coureur (px monde)

/** n pièces (6..16) qui jaillissent du trou : somme des valeurs = total, EXACTEMENT. */
export function coinRainPlan(total: number, seed: number, ox: number, oy: number): RainCoin[] {
  const rand = mulberry32(seed)
  const n = Math.max(6, Math.min(16, Math.round(total / 2.5)))
  const base = Math.floor(total / n)
  const extra = total - base * n // les `extra` premières valent +1 : rien ne se perd
  const out: RainCoin[] = []
  for (let i = 0; i < n; i++) {
    const a = -Math.PI / 2 + (rand() - 0.5) * (Math.PI * 0.62) // éventail vers le haut (±55°)
    const sp = 0.14 + rand() * 0.12
    out.push({ x: ox, y: oy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 0.06, value: base + (i < extra ? 1 : 0), taken: false })
  }
  return out
}

/** Un pas de physique + collecte. `floorY` = plancher de pose (le sol de l'ÉTAGE où la pluie a
 *  éclaté — profondeur oblige). Renvoie les INDEX des pièces ramassées pendant ce pas. */
export function coinRainStep(coins: RainCoin[], runnerCx: number, runnerCy: number, dtMs: number, floorY = RAIN_FLOOR): number[] {
  const taken: number[] = []
  for (let i = 0; i < coins.length; i++) {
    const c = coins[i]!
    if (c.taken) continue
    if (c.vx !== 0 || c.vy !== 0) {
      c.x += c.vx * dtMs
      c.vy += RAIN_G * dtMs
      c.y += c.vy * dtMs
      if (c.x < 54) { c.x = 54; c.vx = -c.vx * 0.6 }
      if (c.x > 1290) { c.x = 1290; c.vx = -c.vx * 0.6 }
      if (c.y >= floorY && c.vy > 0) {
        c.y = floorY
        c.vy = -c.vy * 0.45 // rebond amorti
        c.vx *= 0.8
        if (Math.abs(c.vy) < 0.03) { c.vy = 0; c.vx = 0 } // posée : elle attend le coureur
      }
    }
    const dx = c.x - runnerCx, dy = c.y - runnerCy
    if (dx * dx + dy * dy < RAIN_GRAB * RAIN_GRAB) { c.taken = true; taken.push(i) }
  }
  return taken
}

// --- Layout du sol unique (Lot 2) -------------------------------------------------------------
// Le sol de la Mine est une bande CONTINUE : ces incrustations (peintes par mine-render dans la
// bande hors-écran) sont posées ici par un layout pur et déterministe. La moitié est volontairement
// calée À CHEVAL sur les jointures de cases : l'œil ne peut plus reconstituer la grille. Les 6
// premières couvrent les 6 natures (le sol est TOUJOURS varié, quel que soit le seed).
export type SoilKind = 'filon' | 'gemme' | 'fossile' | 'racine' | 'crane' | 'rail'
export interface SoilFeature { xWorld: number; kind: SoilKind; size: number; tint: number }

const SOIL_KINDS: readonly SoilKind[] = ['filon', 'gemme', 'racine', 'fossile', 'crane', 'rail']

function pickSoilKind(rand: () => number): SoilKind {
  const t = rand() * 100 // pondéré : le filon d'or domine (c'est une mine), les os/rails sont rares
  return t < 30 ? 'filon' : t < 50 ? 'gemme' : t < 65 ? 'fossile' : t < 80 ? 'racine' : t < 90 ? 'crane' : 'rail'
}

function soilSize(kind: SoilKind, rand: () => number): number {
  switch (kind) {
    case 'filon': return 30 + rand() * 24
    case 'gemme': return 14 + rand() * 10
    case 'fossile': return 18 + rand() * 12
    case 'racine': return 24 + rand() * 16
    case 'crane': return 16 + rand() * 6
    case 'rail': return 40 + rand() * 20
  }
}

/** Incrustations du sol : denses (>= 16), espacées d'au moins 20 px monde, dans [48, 1296]. */
export function soilFeatures(seed: number): SoilFeature[] {
  const rand = mulberry32(seed)
  const out: SoilFeature[] = []
  let x = 90 // démarre col 2 : le premier filon (signature de la mine) est visible au spawn, hors du mur
  let last = -1000
  for (let i = 0; x <= 1296; i++) {
    // 2 sur 3 : calée sur la jointure de case la plus proche (à cheval), le reste libre
    let fx = i % 3 !== 2 ? Math.round(x / 48) * 48 + Math.round((rand() - 0.5) * 12) : Math.round(x)
    if (fx - last < 20) fx = last + 20
    if (fx >= 48 && fx <= 1296) {
      const kind = i < SOIL_KINDS.length ? SOIL_KINDS[i]! : pickSoilKind(rand)
      out.push({ xWorld: fx, kind, size: Math.round(soilSize(kind, rand)), tint: Math.floor(rand() * 4) })
      last = fx
    }
    x += 44 + rand() * 36
  }
  return out
}
